/**
 * Publish feature — API client helpers.
 *
 * All auth-required endpoints route through the shared axios instance
 * (`api`). Public endpoints hit BACKEND_URL directly without cookies to
 * avoid unnecessary CORS preflights and keep the public feed SSR-safe.
 */
import api, { API_BASE } from "@/lib/api";

import { devWarn } from "@/lib/devLog";
// ---------- Posts ----------
const stripEmpty = (params) => {
  const out = {};
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") out[k] = v;
  });
  return out;
};

export const listPosts = async (params = {}) => {
  const { data } = await api.get("/publish/posts", { params: stripEmpty(params) });
  return data;
};

export const getPost = async (postId) => {
  const { data } = await api.get(`/publish/posts/${postId}`);
  return data;
};

export const createPost = async (body) => {
  const { data } = await api.post("/publish/posts", body);
  return data;
};

export const updatePost = async (postId, body) => {
  const { data } = await api.patch(`/publish/posts/${postId}`, body);
  return data;
};

export const publishPost = async (postId) => {
  const { data } = await api.post(`/publish/posts/${postId}/publish`);
  return data;
};

export const deletePost = async (postId) => {
  const { data } = await api.delete(`/publish/posts/${postId}`);
  return data;
};

export const incrementView = async (postId) => {
  try { await api.post(`/publish/posts/${postId}/view`); }
  catch (err) {
    // View counter is best-effort (not shown to the user immediately).
    // Log so it's still visible in the console for observability.
    devWarn("[publish] view counter bump failed", err);
  }
};

export const sharePost = async (postId) => {
  const { data } = await api.post(`/publish/posts/${postId}/share`);
  return data;
};

export const delinkPost = async (postId) => {
  const { data } = await api.post(`/publish/posts/${postId}/delink`);
  return data;
};

export const republishPost = async (postId) => {
  const { data } = await api.post(`/publish/posts/${postId}/publish`);
  return data;
};

// ---------- Engagement ----------
export const setReaction = async (postId, reaction) => {
  const { data } = await api.post(`/publish/posts/${postId}/reactions`, { reaction });
  return data;
};

export const removeReaction = async (postId) => {
  const { data } = await api.delete(`/publish/posts/${postId}/reactions`);
  return data;
};

export const listComments = async (postId) => {
  const { data } = await api.get(`/publish/posts/${postId}/comments`);
  return data;
};

export const addComment = async (postId, body) => {
  const { data } = await api.post(`/publish/posts/${postId}/comments`, { body });
  return data;
};

export const deleteComment = async (commentId) => {
  const { data } = await api.delete(`/publish/comments/${commentId}`);
  return data;
};

// ---------- Reports / Bookmarks / Follows ----------
export const reportPost = async (postId, reason, category = "other") => {
  const { data } = await api.post(`/publish/posts/${postId}/report`, { reason, category });
  return data;
};

export const toggleBookmark = async (postId) => {
  const { data } = await api.post(`/publish/posts/${postId}/bookmark`);
  return data;
};

export const listBookmarks = async () => {
  const { data } = await api.get("/publish/bookmarks");
  return data;
};

export const follow = async (target_kind, target_id) => {
  const { data } = await api.post("/publish/follows", { target_kind, target_id });
  return data;
};

export const unfollow = async (target_kind, target_id) => {
  const { data } = await api.delete("/publish/follows", { params: { target_kind, target_id } });
  return data;
};

export const listFollows = async () => {
  const { data } = await api.get("/publish/follows");
  return data;
};

export const getSettings = async () => {
  const { data } = await api.get("/publish/settings");
  return data;
};

// ---------- Super-admin moderation ----------
export const modGetSettings = async () => {
  const { data } = await api.get("/publish/moderation/settings");
  return data;
};

export const modPutSettings = async (body) => {
  const { data } = await api.put("/publish/moderation/settings", body);
  return data;
};

export const modListReports = async (status = "open", limit = 50, offset = 0) => {
  const { data } = await api.get("/publish/moderation/reports",
    { params: { status, limit, offset } });
  return data;
};

export const modListPosts = async (params = {}) => {
  const { data } = await api.get("/publish/moderation/posts", { params: stripEmpty(params) });
  return data;
};

export const modHidePost = async (postId, note = "") => {
  const { data } = await api.post(`/publish/moderation/posts/${postId}/hide`, { note });
  return data;
};

export const modUnhidePost = async (postId, note = "") => {
  const { data } = await api.post(`/publish/moderation/posts/${postId}/unhide`, { note });
  return data;
};

export const modDeletePost = async (postId) => {
  const { data } = await api.delete(`/publish/moderation/posts/${postId}`);
  return data;
};

export const modResolveReport = async (reportId, action = "dismiss") => {
  const { data } = await api.post(`/publish/moderation/reports/${reportId}/resolve`,
    null, { params: { action } });
  return data;
};

// ---------- Public (no auth) ----------
export const publicFeed = async (params = {}) => {
  const url = new URL(`${API_BASE}/publish/public/feed`);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, v);
  });
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`Feed ${res.status}`);
  return res.json();
};

export const publicPost = async (postId) => {
  const res = await fetch(`${API_BASE}/publish/public/posts/${postId}`);
  if (!res.ok) throw new Error(`Post ${res.status}`);
  return res.json();
};

export const publicTopics = async () => {
  const res = await fetch(`${API_BASE}/publish/public/topics`);
  if (!res.ok) return { topics: [] };
  return res.json();
};

export const publicSettings = async () => {
  const res = await fetch(`${API_BASE}/publish/public/settings`);
  if (!res.ok) return { public_landing_enabled: true, default_topics: [] };
  return res.json();
};
